import React, { ReactNode, memo, MouseEvent } from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { useTranslation } from 'react-i18next';
import FormWrapper from './form-wrapper';

export interface DialogWrapperProps {
  // Core props
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  show?: boolean; // Legacy prop - maps to open
  onHide?: () => void; // Legacy prop - maps to onOpenChange (used by both ModalWindow and ModalContent)
  onClose?: () => void; // Legacy prop - maps to onOpenChange

  // Content props
  title?: string | ReactNode;
  description?: string | ReactNode;
  children?: ReactNode; // Trigger element (optional when controlled)
  content?: ReactNode; // Optional content for the dialog body

  // Footer props (for ModalContent compatibility)
  footer?: ReactNode;
  onConfirm?: ((event: MouseEvent<HTMLButtonElement>) => void) | (() => void) | (() => Promise<void>);
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonDisabled?: boolean;
  confirmButtonLoading?: boolean;
  confirmButtonType?: 'button' | 'submit';
  confirmButtonForm?: string;
  hideFooter?: boolean;
  hideConfirm?: boolean;
  customFooter?: ReactNode;
  footerChildren?: ReactNode;

  // Size and styling
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'small' | 'large' | 'fill' | 'lgsmall'; // Support legacy sizes
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
  headerClassName?: string;
  // Close behavior
  closeOnOutsideClick?: boolean; // Default: true
  closeOnEscape?: boolean; // Default: true
  disableRootClose?: boolean; // Legacy prop - maps to closeOnOutsideClick
  showCloseButton?: boolean;

  // Legacy Modal props
  trigger?: ReactNode; // Legacy ModalWindow trigger prop
  onShow?: () => void;
  onEnter?: () => void;
  onExit?: () => void;
  onExited?: () => void;
  hideHeader?: boolean;
  headerContent?: ReactNode;
  fullWidthContent?: boolean;

  // Form wrapper props (for Modal compatibility)
  wrapper?: React.ComponentType<any>; // e.g., Modal.FormWrapper
  wrapperProps?: Record<string, any>; // Props to pass to wrapper
  onBack?: () => void; // Back button handler

  // Dialog behavior
  modal?: boolean; // Control focus trap behavior (default: true, set to false in tests)
}

const sizeClasses: Record<string, string> = {
  xs: 'tw-w-[450px] tw-min-w-[450px] tw-max-w-[450px]',
  sm: 'tw-w-[550px] tw-min-w-[550px] tw-max-w-[550px]',
  md: 'tw-w-[700px] tw-min-w-[700px] tw-max-w-[700px]',
  lg: 'tw-w-[1100px] tw-min-w-[1100px] tw-max-w-[1100px]', // Large size for modals with more content
  small: 'tw-w-[550px] tw-min-w-[550px] tw-max-w-[550px]', // Legacy
  large: 'tw-w-[1100px] tw-min-w-[1100px] tw-max-w-[1100px]', // Legacy - standard large size (same as lg)
  fill: 'tw-w-[min(90vw,1200px)] tw-min-w-[min(90vw,1200px)] tw-max-w-[min(90vw,1200px)]', // Extra large for tables and wide content
  lgsmall: 'tw-w-[824px] tw-min-w-[824px] tw-max-w-[824px]',
};

function DialogWrapperComponent(props: DialogWrapperProps) {
  const {
    // Core props (new API)
    open: openProp,
    onOpenChange: onOpenChangeProp,
    // Legacy props
    show,
    onHide: onHideProp,
    onClose: onCloseProp,
    // Content
    title,
    description,
    children,
    content,
    // Footer props (ModalContent compatibility)
    footer: footerProp,
    onConfirm,
    confirmButtonText,
    cancelButtonText,
    confirmButtonDisabled = false,
    confirmButtonLoading = false,
    confirmButtonType = 'button',
    confirmButtonForm,
    hideFooter = false,
    hideConfirm = false,
    customFooter,
    footerChildren,
    // Size and styling
    size: sizeProp = 'md',
    className,
    bodyClassName,
    footerClassName,
    headerClassName,
    // Close behavior
    closeOnOutsideClick = true,
    closeOnEscape = true,
    disableRootClose, // Legacy
    showCloseButton = true,
    // Legacy Modal props
    trigger, // Legacy ModalWindow trigger
    onShow,
    onEnter,
    onExit,
    onExited,
    hideHeader = false,
    headerContent,
    fullWidthContent = false,
    // Form wrapper props
    wrapper,
    wrapperProps,
    onBack,
    // Dialog behavior
    modal = true,
  } = props;

  const { t } = useTranslation();
  const isControlled = typeof openProp !== 'undefined' || typeof show !== 'undefined';
  const [internalOpen, setInternalOpen] = React.useState(false);

  // Map legacy props to new API
  const open = openProp !== undefined ? openProp : (show !== undefined ? show : undefined);
  const effectiveCloseOnOutsideClick = disableRootClose !== undefined ? !disableRootClose : closeOnOutsideClick;

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }

    // Call onOpenChange if provided
    if (onOpenChangeProp) {
      onOpenChangeProp(newOpen);
    }

    // Call legacy callbacks
    if (!newOpen) {
      if (onHideProp) {
        onHideProp();
      }
      if (onCloseProp) {
        onCloseProp();
      }
      if (onExit) {
        onExit();
      }
      if (onExited) {
        // Delay to match old modal behavior
        setTimeout(() => onExited(), 250);
      }
    } else {
      if (onShow) {
        onShow();
      }
      if (onEnter) {
        onEnter();
      }
    }
  }, [isControlled, onOpenChangeProp, onHideProp, onCloseProp, onShow, onEnter, onExit, onExited]);

  const isOpen = isControlled ? (open ?? false) : internalOpen;

  // Handle size mapping
  const size = sizeClasses[sizeProp] ? sizeProp : 'md';
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Determine trigger - children or legacy trigger prop
  const triggerElement = trigger || children;
  const defaultFooterClassName = cn(`tw-px-5 tw-py-4 tw-flex-shrink-0 tw-border-t tw-border-solid
    tw-border-[#E4E4E7] tw-border-0`, footerClassName);
  // Build footer content
  const renderFooter = () => {
    if (hideFooter) return null;

    if (customFooter) {
      return <DialogFooter className={defaultFooterClassName}>{customFooter}</DialogFooter>;
    }

    // Handle footer as a function (legacy API compatibility)
    if (footerProp && typeof footerProp === 'function') {
      const handleHide = () => handleOpenChange(false);
      const footerContent = footerProp(handleHide);
      return <DialogFooter className={defaultFooterClassName}>{footerContent}</DialogFooter>;
    }

    // Auto-generate footer from ModalContent props
    if (footerProp || onConfirm || onHideProp || onCloseProp || onBack) {
      const footerContent = (
        <div className="tw-flex tw-gap-2 tw-justify-end">
          {onBack && (
            <Button onClick={onBack}>
              {t('common:back')}
            </Button>
          )}
          {(onHideProp || onCloseProp) && !hideConfirm && (
            <Button onClick={() => handleOpenChange(false)}>
              {cancelButtonText || t('common:modal_footer_button_cancel')}
            </Button>
          )}
          {footerChildren}
          {onConfirm && !hideConfirm && (
            <Button
              onClick={onConfirm}
              type="primary"
              disabled={confirmButtonDisabled}
              isLoading={confirmButtonLoading}
              buttonType={confirmButtonType}
              form={confirmButtonForm}
            >
              {confirmButtonText || t('core:confirm')}
            </Button>
          )}
        </div>
      );
      return <DialogFooter className={defaultFooterClassName}>{footerContent}</DialogFooter>;
    }

    // Use provided footer (ReactNode)
    if (footerProp) {
      return <DialogFooter className={defaultFooterClassName}>{footerProp}</DialogFooter>;
    }

    return null;
  };

  // Handle prevent close
  const handleInteractOutside = React.useCallback((event: Event) => {
    if (!effectiveCloseOnOutsideClick) {
      event.preventDefault();
    }
  }, [effectiveCloseOnOutsideClick]);

  const handleEscapeKeyDown = React.useCallback((event: KeyboardEvent) => {
    if (!closeOnEscape) {
      event.preventDefault();
    }
  }, [closeOnEscape]);

  // Wrap content with FormWrapper if provided
  const WrapperComponent = wrapper || (wrapperProps ? FormWrapper : null);
  const shouldUseWrapper = WrapperComponent && wrapperProps;

  const dialogBodyContent = (
    <div className={cn(
      'tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-min-h-0 tw-p-4',
      fullWidthContent && 'tw-w-full',
      bodyClassName
    )}
    >
      {content}
    </div>
  );

  const renderHeader = () => {
    if (title || description || headerContent) {
      return (
        <DialogHeader className={cn('tw-border-b tw-border-solid tw-border-[#E4E4E7] tw-border-0 tw-p-5', headerClassName)}>
          {title ? (
            <DialogTitle className={hideHeader ? 'tw-sr-only' : undefined}>
              {title}
            </DialogTitle>
          ) : (
            <DialogTitle className="tw-sr-only" />
          )}
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="tw-sr-only" />
          )}
          {headerContent}
        </DialogHeader>
      );
    }
    return (
      <DialogHeader>
        <DialogTitle className="tw-sr-only" />
        <DialogDescription className="tw-sr-only" />
      </DialogHeader>
    );
  };

  const footer = renderFooter();
  const header = renderHeader();

  const dialogContent = (
    <>
      {header}
      {dialogBodyContent}
      {footer}
    </>
  );
  // When using a wrapper, wrap both content and footer so submit buttons work
  const dialogInnerContent = shouldUseWrapper ? (
    <WrapperComponent {...wrapperProps}>
      {dialogContent}
    </WrapperComponent>
  ) : (
    <>
      {dialogContent}
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      {triggerElement && (
        <DialogTrigger asChild>
          {triggerElement}
        </DialogTrigger>
      )}
      <DialogContent
        className={cn(sizeClass, className)}
        showCloseButton={showCloseButton}
        onInteractOutside={!effectiveCloseOnOutsideClick ? handleInteractOutside : undefined}
        onEscapeKeyDown={!closeOnEscape ? handleEscapeKeyDown : undefined}
      >

        {dialogInnerContent}
      </DialogContent>
    </Dialog>
  );
}

export const DialogWrapper = memo(DialogWrapperComponent);

